import { createFrameResolver } from '../../model/resolve-frame';
import type { Layer, PieceModel } from '../../model/types';
import { createMp4Encoder, isMp4EncodingSupported } from './encode-mp4';
import { createOffscreenRenderer } from './offscreen-renderer';
import { renderPieceAudio } from './render-audio';
import { videoBitrateFor } from './video-bitrate';

export type ExportPieceOptions = {
  model: PieceModel;
  onProgress?: (progress: number) => void;
  isCancelled?: () => boolean;
};

export class ExportCancelled extends Error {
  constructor() {
    super('Export cancelled');
  }
}

export type ExportNotice = { title: string; reason?: string };

export const FAILURE_TITLE = 'El video no se generó';

export const CORS_REASON = 'Alguna imagen o video de la pieza no cargó por permisos CORS.';

const UNSUPPORTED_BROWSER: ExportNotice = { title: 'Este navegador no puede exportar videos' };

const UNSUPPORTED_EFFECT: ExportNotice = {
  title: 'Esta pieza todavía no se puede exportar',
  reason: 'El efecto de desvanecido no se puede incluir en un video.'
};

export class TaintedSources extends Error {
  constructor() {
    super(CORS_REASON);
  }
}

// Cross-origin pixels can't be read back, and the encoder would fail later with nothing useful to say.
const isTainted = (element: HTMLCanvasElement): boolean => {
  try {
    element.getContext('2d')?.getImageData(0, 0, 1, 1);
    return false;
  } catch {
    return true;
  }
};

const hasAlphaFalloff = (layers: Layer[]): boolean =>
  layers.some(layer => {
    if (layer.kind === 'group') return !!layer.content.alphaFalloff || hasAlphaFalloff(layer.content.children);
    return 'alphaFalloff' in layer.content && !!layer.content.alphaFalloff;
  });

/**
 * Why this piece cannot be printed, if it cannot. Alpha falloff bakes its overlay
 * in a React hook the printer doesn't run, so it has no way in yet.
 */
export const exportBlocker = (model: PieceModel): ExportNotice | null => {
  if (!isMp4EncodingSupported()) return UNSUPPORTED_BROWSER;
  if (hasAlphaFalloff(Object.values(model.layers))) return UNSUPPORTED_EFFECT;
  return null;
};

/**
 * Prints the piece frame by frame straight from the model. Nothing here reads
 * the clock, so a slow machine or a hidden tab makes the export take longer —
 * never wrong.
 */
export const exportPieceToMp4 = async ({ model, onProgress, isCancelled }: ExportPieceOptions): Promise<Blob> => {
  // Mixing the audio and loading the media touch the same files, so start both at once.
  const audioPromise = renderPieceAudio(model);
  audioPromise.catch(() => undefined); // handled here so a renderer failure doesn't surface it as unhandled

  const renderer = await createOffscreenRenderer(model);

  try {
    if (isTainted(renderer.element)) throw new TaintedSources();

    const audio = await audioPromise;

    const resolveFrame = createFrameResolver(model, renderer.canonical);
    const encoder = await createMp4Encoder({
      width: model.width,
      height: model.height,
      fps: model.fps,
      bitrate: videoBitrateFor(model.width, model.height, model.fps),
      audio
    });

    const totalFrames = Math.max(1, Math.round((model.duration / 1000) * model.fps));

    for (let index = 0; index < totalFrames; index++) {
      if (isCancelled?.()) {
        encoder.abort();
        throw new ExportCancelled();
      }

      await renderer.drawFrame(resolveFrame((index * 1000) / model.fps));
      await encoder.addFrame(renderer.element, index);
      onProgress?.((index + 1) / totalFrames);
    }

    return await encoder.finish();
  } finally {
    renderer.dispose();
  }
};
